import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Accordion, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import { apolloClientMeBooking } from "@me-team/host/src/apollo/apollo-client";
import {
    useRestaurantAddRequestChangeStatusMutation,
    useRestaurantDeleteRequestChangeStatusMutation,
    useRestaurantAddRequestSetInternalNoteMutation,
    useRestaurantDeleteRequestSetInternalNoteMutation,
} from "@me-team/host/meBooking/restaurantRequest/graphql/restaurantRequest.hooks";
import { RestaurantAddRequestStatus, RestaurantDeleteRequestStatus } from "@me-team/host/meBooking/graphql/types";
import { AddRequestItem, DeleteRequestItem } from "./RestaurantRequest";
import TableIcon from "./svg/TableIcon";

type AddRequestStatus = RestaurantAddRequestStatus;
type DeleteRequestStatus = RestaurantDeleteRequestStatus;

interface StatusOption {
    label: string;
    color: string;
    border: string;
    text: string;
    bg: string;
}

const STATUS_STYLE: Record<string, Omit<StatusOption, "label">> = {
    new: { color: "bg-warning-light", border: "border-warning-dark", text: "text-warning-dark", bg: "bg-warning-dark" },
    review: { color: "bg-violet", border: "border-primary", text: "text-primary", bg: "bg-primary" },
    done: { color: "bg-additional-green", border: "border-green", text: "text-green", bg: "bg-green" },
    rejected: { color: "bg-danger-light", border: "border-danger", text: "text-danger", bg: "bg-danger" },
};

const ADD_STATUS_MAP: Record<AddRequestStatus, StatusOption> = {
    [RestaurantAddRequestStatus.New]: { label: "New", ...STATUS_STYLE.new },
    [RestaurantAddRequestStatus.Reviewed]: { label: "Under Review", ...STATUS_STYLE.review },
    [RestaurantAddRequestStatus.Contacted]: { label: "Under Review", ...STATUS_STYLE.review },
    [RestaurantAddRequestStatus.Approved]: { label: "Processed", ...STATUS_STYLE.done },
    [RestaurantAddRequestStatus.Rejected]: { label: "Rejected", ...STATUS_STYLE.rejected },
};

const DELETE_STATUS_MAP: Record<DeleteRequestStatus, StatusOption> = {
    [RestaurantDeleteRequestStatus.New]: { label: "New", ...STATUS_STYLE.new },
    [RestaurantDeleteRequestStatus.UnderReview]: { label: "Under Review", ...STATUS_STYLE.review },
    [RestaurantDeleteRequestStatus.Verified]: { label: "Processed", ...STATUS_STYLE.done },
    [RestaurantDeleteRequestStatus.Processed]: { label: "Processed", ...STATUS_STYLE.done },
    [RestaurantDeleteRequestStatus.Rejected]: { label: "Rejected", ...STATUS_STYLE.rejected },
};

const ADD_STATUS_OPTIONS: AddRequestStatus[] = [
    RestaurantAddRequestStatus.New,
    RestaurantAddRequestStatus.Reviewed,
    RestaurantAddRequestStatus.Approved,
    RestaurantAddRequestStatus.Rejected,
];

const DELETE_STATUS_OPTIONS: DeleteRequestStatus[] = [
    RestaurantDeleteRequestStatus.New,
    RestaurantDeleteRequestStatus.UnderReview,
    RestaurantDeleteRequestStatus.Processed,
    RestaurantDeleteRequestStatus.Rejected,
];

interface RequestModalProps {
    show: boolean;
    handleClose: () => void;
    request: (AddRequestItem & { requestType: "add" }) | (DeleteRequestItem & { requestType: "delete" }) | null;
    refetch: () => void;
}

const RequestModal: React.FC<RequestModalProps> = ({ show, handleClose, request, refetch }) => {
    const { t } = useTranslation();

    const isAdd = request?.requestType === "add";
    const statusMap = isAdd ? ADD_STATUS_MAP : DELETE_STATUS_MAP;

    const [status, setStatus] = useState<AddRequestStatus | DeleteRequestStatus | null>(null);
    const [internalNote, setInternalNote] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (request) {
            setStatus(request.status as AddRequestStatus | DeleteRequestStatus);
            setInternalNote(request.internalNote ?? "");
            setError(null);
        }
    }, [request]);

    const [changeAddStatus, { loading: addStatusLoading }] = useRestaurantAddRequestChangeStatusMutation({
        client: apolloClientMeBooking,
    });
    const [changeDeleteStatus, { loading: deleteStatusLoading }] = useRestaurantDeleteRequestChangeStatusMutation({
        client: apolloClientMeBooking,
    });
    const [setAddNote, { loading: addNoteLoading }] = useRestaurantAddRequestSetInternalNoteMutation({
        client: apolloClientMeBooking,
    });
    const [setDeleteNote, { loading: deleteNoteLoading }] = useRestaurantDeleteRequestSetInternalNoteMutation({
        client: apolloClientMeBooking,
    });

    const loading = addStatusLoading || deleteStatusLoading || addNoteLoading || deleteNoteLoading;

    const handleSubmit = async () => {
        if (!request || !status) return;
        setError(null);
        try {
            if (isAdd) {
                await Promise.all([
                    changeAddStatus({ variables: { id: request.id, status: status as AddRequestStatus } }),
                    setAddNote({ variables: { id: request.id, internalNote } }),
                ]);
            } else {
                await Promise.all([
                    changeDeleteStatus({ variables: { id: request.id, status: status as DeleteRequestStatus } }),
                    setDeleteNote({ variables: { id: request.id, internalNote } }),
                ]);
            }
            await refetch();
            handleClose();
        } catch (err) {
            console.error(err);
            setError(t("Something went wrong. Please try again."));
        }
    };

    if (!request) return null;

    const currentStatusOption = statusMap[status as keyof typeof statusMap];
    const isUnchanged = status === request.status && internalNote === (request.internalNote ?? "");

    return (
        <Modal scrollable show={show} centered onHide={handleClose}>
            <Modal.Header className="border-0" closeButton>
                <div>
                    <Modal.Title className="fs-24 d-flex align-items-center gap-2">
                        {isAdd ? t("Add venue") : t("Delete venue")}
                        {currentStatusOption && (
                            <span
                                className={`fs-7 d-inline-block px-2 py-1 rounded border ${currentStatusOption.border} ${currentStatusOption.text} ${currentStatusOption.color}`}
                            >
                                {t(currentStatusOption.label)}
                            </span>
                        )}
                    </Modal.Title>
                    <span className="fs-7 text-grey">
                        {t("Issued")}: {moment(request.createdAt).format("DD.MM.YYYY, HH:mm")} • ID: {request.id}
                    </span>
                </div>
            </Modal.Header>

            <Modal.Body>
                <Accordion className="mb-3 accordion-booking" defaultActiveKey={["0", "1"]} alwaysOpen>
                    <Accordion.Item className="border rounded mb-3" eventKey="0">
                        <Accordion.Header>{t("Venue information")}</Accordion.Header>
                        <Accordion.Body className="pb-4 text-dark">
                            <div className="d-flex align-items-center mb-3">
                                <TableIcon />
                                <p className="mb-0 fw-bold">{request.venueName}</p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-geo-alt me-2 flex-shrink-0"></i>
                                <p className="mb-0">
                                    {[request.address, request.city, request.countryName].filter(Boolean).join(", ")}
                                </p>
                            </div>
                            {request.website && (
                                <div className="d-flex mb-3">
                                    <i className="bi bi-globe me-2 flex-shrink-0"></i>
                                    <a
                                        href={request.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mb-0 text-break"
                                    >
                                        {request.website}
                                    </a>
                                </div>
                            )}
                            {request.googleMapsUrl && (
                                <div className="d-flex mb-3">
                                    <i className="bi bi-link-45deg me-2 flex-shrink-0"></i>
                                    <a
                                        href={request.googleMapsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mb-0 text-break"
                                    >
                                        {request.googleMapsUrl}
                                    </a>
                                </div>
                            )}
                            {!isAdd && (request as DeleteRequestItem).reasonLabel && (
                                <div className="d-flex mb-3">
                                    <i className="bi bi-exclamation-circle me-2 flex-shrink-0"></i>
                                    <p className="mb-0">
                                        {t("Reason")}: {(request as DeleteRequestItem).reasonLabel}
                                    </p>
                                </div>
                            )}
                            {request.comment && (
                                <div className="d-flex">
                                    <i className="bi bi-card-text me-2 flex-shrink-0"></i>
                                    <p className="mb-0">{request.comment}</p>
                                </div>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item className="border rounded mb-3" eventKey="1">
                        <Accordion.Header>{t("Contact person")}</Accordion.Header>
                        <Accordion.Body className="pb-4 text-dark">
                            <div className="d-flex mb-3">
                                <i className="bi bi-person me-2 flex-shrink-0"></i>
                                <p className="mb-0 fw-bold">
                                    {request.contactName}
                                    {request.role && <span className="fw-normal"> / {request.role}</span>}
                                </p>
                            </div>
                            <div className="d-flex mb-3">
                                <i className="bi bi-telephone me-2 flex-shrink-0"></i>
                                <a className="mb-0" href={`tel:${request.contactPhone}`}>
                                    {request.contactPhone}
                                </a>
                            </div>
                            <div className="d-flex">
                                <i className="bi bi-envelope me-2 flex-shrink-0"></i>
                                <a className="mb-0 text-break" href={`mailto:${request.contactEmail}`}>
                                    {request.contactEmail}
                                </a>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <div className="border rounded p-3">
                    <p className="fw-bold fs-20">{t("Application status")}</p>
                    <Dropdown className="mb-4 status-dd">
                        <Dropdown.Toggle
                            className="w-100 rounded-3 bg-white border-secondary text-dark d-flex justify-content-between"
                            id="request-status-dropdown"
                        >
                            <span className="d-flex align-items-center fs-16">
                                {currentStatusOption && (
                                    <span
                                        className={`d-inline-block me-2 rounded-1 status-dd-icon ${currentStatusOption.bg}`}
                                    />
                                )}
                                {currentStatusOption ? t(currentStatusOption.label) : status}
                            </span>
                            <i className="bi bi-chevron-down"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="w-100">
                            {(isAdd ? ADD_STATUS_OPTIONS : DELETE_STATUS_OPTIONS).map((key) => {
                                const option = statusMap[key as keyof typeof statusMap];
                                return (
                                    <Dropdown.Item
                                        key={key}
                                        className="py-2"
                                        onClick={() => setStatus(key as AddRequestStatus | DeleteRequestStatus)}
                                    >
                                        <span className="d-flex align-items-center">
                                            <span
                                                className={`d-inline-block me-2 rounded-1 status-dd-icon ${option.bg}`}
                                            />
                                            {t(option.label)}
                                        </span>
                                    </Dropdown.Item>
                                );
                            })}
                        </Dropdown.Menu>
                    </Dropdown>

                    <div className="form-floating">
                        <textarea
                            className="form-control rounded-3"
                            placeholder={t("Manager note")}
                            id="request-internal-note"
                            style={{ height: "110px" }}
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                        />
                        <label htmlFor="request-internal-note">{t("Manager note")}</label>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0">
                {error && <div className="w-100 alert alert-danger py-2 mb-2">{error}</div>}
                <Row className="w-100 gx-3">
                    <Col sm={6} className="ps-lg-0 mb-3 mb-lg-0">
                        <Button className="w-100" variant="outline-primary" onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                    </Col>
                    <Col sm={6} className="pe-lg-0">
                        <Button
                            className="w-100"
                            variant="primary"
                            disabled={isUnchanged || loading}
                            onClick={handleSubmit}
                        >
                            {t("Save changes")}
                        </Button>
                    </Col>
                </Row>
            </Modal.Footer>
        </Modal>
    );
};

export default RequestModal;
